import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config';

function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [timeBlocks, setTimeBlocks] = useState([]);
  const [filter, setFilter] = useState('all');
  const [stylistFilter, setStylistFilter] = useState('all');
  const [view, setView] = useState('week'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAfterHours, setShowAfterHours] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [draggedAppointment, setDraggedAppointment] = useState(null);
  const [editedAppointment, setEditedAppointment] = useState(null);
  const [appointmentPayments, setAppointmentPayments] = useState([]);
  const [editedServices, setEditedServices] = useState([]);
  const [serviceToAdd, setServiceToAdd] = useState('');
  const [editedTotalDuration, setEditedTotalDuration] = useState(0);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockFormData, setBlockFormData] = useState({
    stylist_id: '',
    block_date: '',
    start_time: '',
    end_time: '',
    reason: ''
  });

  useEffect(() => {
    fetchAppointments();
    fetchServices();
    fetchStylists();
    fetchTimeBlocks();
  }, []);

  const fetchAppointments = async () => {
    const response = await axios.get(`${API_URL}/appointments`);
    setAppointments(response.data);
  };

  const fetchServices = async () => {
    const response = await axios.get(`${API_URL}/services`);
    setServices(response.data);
  };

  const fetchStylists = async () => {
    try {
      const response = await axios.get(`${API_URL}/stylists/admin`);
      setStylists(response.data);
    } catch (error) {
      console.error('Error fetching stylists:', error);
    }
  };

  const fetchTimeBlocks = async () => {
    try {
      const response = await axios.get(`${API_URL}/time-blocks`);
      setTimeBlocks(response.data);
    } catch (error) {
      console.error('Error fetching time blocks:', error);
    }
  };

  const handleCreateTimeBlock = async (e) => {
    e.preventDefault();

    // Validate time range
    if (blockFormData.start_time >= blockFormData.end_time) {
      alert('End time must be after start time');
      return;
    }

    try {
      await axios.post(`${API_URL}/time-blocks`, blockFormData);
      setShowBlockModal(false);
      setBlockFormData({
        stylist_id: '',
        block_date: '',
        start_time: '',
        end_time: '',
        reason: ''
      });
      fetchTimeBlocks();
    } catch (error) {
      console.error('Error creating time block:', error);
      alert('Failed to create time block: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteTimeBlock = async (blockId) => {
    if (!window.confirm('Are you sure you want to delete this time block?')) return;

    try {
      await axios.delete(`${API_URL}/time-blocks/${blockId}`);
      fetchTimeBlocks();
    } catch (error) {
      console.error('Error deleting time block:', error);
      alert('Failed to delete time block');
    }
  };

  const updateStatus = async (id, status) => {
    await axios.put(`${API_URL}/appointments/${id}`, {
      appointment_date: appointments.find(a => a.id === id).appointment_date,
      appointment_time: appointments.find(a => a.id === id).appointment_time,
      status,
      notes: appointments.find(a => a.id === id).notes
    });
    fetchAppointments();
  };

  const deleteAppointment = async (id) => {
    if (window.confirm('Delete this appointment?')) {
      await axios.delete(`${API_URL}/appointments/${id}`);
      fetchAppointments();
    }
  };

  const updateAppointmentTime = async (id, newDate, newTime) => {
    const apt = appointments.find(a => a.id === id);
    await axios.put(`${API_URL}/appointments/${id}`, {
      appointment_date: newDate,
      appointment_time: newTime,
      status: apt.status,
      notes: apt.notes
    });
    fetchAppointments();
  };

  const updateAppointmentDetails = async () => {
    if (!editedAppointment) return;

    // Prepare the update payload
    const updatePayload = {
      appointment_date: editedAppointment.appointment_date,
      appointment_time: editedAppointment.appointment_time,
      status: editedAppointment.status,
      notes: editedAppointment.notes,
      stylist_id: editedAppointment.stylist_id
    };

    // If services were edited (admin booking with multiple services)
    if (editedServices.length > 0) {
      updatePayload.service_ids = editedServices.map(s => s.id);
      updatePayload.custom_duration = editedTotalDuration;
    } else {
      // Single service (public booking or single service appointment)
      updatePayload.service_id = editedAppointment.service_id;
      updatePayload.custom_duration = editedAppointment.service_duration;
    }

    await axios.put(`${API_URL}/appointments/${editedAppointment.id}`, updatePayload);
    fetchAppointments();
    setSelectedAppointment(null);
    setEditedAppointment(null);
  };

  const openAppointmentModal = async (apt) => {
    setSelectedAppointment(apt);
    setEditedAppointment({ ...apt });

    // Initialize services for editing
    if (apt.services && apt.services.length > 0) {
      setEditedServices([...apt.services]);
      setEditedTotalDuration(apt.total_duration || apt.service_duration);
    } else {
      setEditedServices([]);
      setEditedTotalDuration(apt.service_duration || 0);
    }
    setServiceToAdd('');

    // Fetch payment information for this appointment
    try {
      const response = await axios.get(`${API_URL}/payments`);
      const payments = response.data.filter(p => p.appointment_id === apt.id);
      setAppointmentPayments(payments);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setAppointmentPayments([]);
    }
  };

  const closeAppointmentModal = () => {
    setSelectedAppointment(null);
    setEditedAppointment(null);
    setAppointmentPayments([]);
    setEditedServices([]);
    setServiceToAdd('');
    setEditedTotalDuration(0);
  };

  const handleAddServiceToAppointment = () => {
    if (!serviceToAdd) return;

    const service = services.find(s => s.id === parseInt(serviceToAdd));
    if (!service) return;

    const newService = {
      id: service.id,
      name: service.name,
      price: service.price,
      duration: service.duration
    };

    const updatedServices = [...editedServices, newService];
    setEditedServices(updatedServices);

    // Update total duration
    const newTotal = editedTotalDuration + service.duration;
    setEditedTotalDuration(newTotal);

    // Reset dropdown
    setServiceToAdd('');
  };

  const handleRemoveServiceFromAppointment = (index) => {
    const serviceToRemove = editedServices[index];
    if (serviceToRemove) {
      const newServices = editedServices.filter((_, i) => i !== index);
      setEditedServices(newServices);
      setEditedTotalDuration(editedTotalDuration - serviceToRemove.duration);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const getPaymentStatusBadge = (status) => {
    const badges = {
      completed: { text: 'Completed', class: 'status-completed' },
      pending: { text: 'Pending', class: 'status-pending' },
      failed: { text: 'Failed', class: 'status-cancelled' },
      refunded: { text: 'Refunded', class: 'status-cancelled' }
    };
    return badges[status] || { text: status, class: '' };
  };

  const filteredAppointments = appointments.filter(apt => {
    // Status filter
    if (filter !== 'all' && apt.status !== filter) return false;

    // Stylist filter
    if (stylistFilter !== 'all') {
      if (stylistFilter === 'unassigned' && apt.stylist_id) return false;
      if (stylistFilter !== 'unassigned' && apt.stylist_id !== parseInt(stylistFilter)) return false;
    }

    return true;
  });

  // Helper functions
  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Convert military time to AM/PM format
  const formatTime = (militaryTime) => {
    const [hours, minutes] = militaryTime.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  const getAppointmentsForDate = (dateStr) => {
    return filteredAppointments.filter(apt => apt.appointment_date === dateStr);
  };

  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (view === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Calculate appointment card position and height
  const getAppointmentStyle = (time, duration) => {
    const [hours, minutes] = time.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes;
    const startMinutes = 8 * 60; // 8 AM
    const minutesFromStart = totalMinutes - startMinutes;

    // Each hour is 60px, so each minute is 1px
    const top = minutesFromStart;
    const height = duration || 60; // Default to 60 minutes if no duration

    return { top: `${top}px`, height: `${height}px` };
  };

  // Navigate to day view for a specific date
  const navigateToDay = (dateStr) => {
    // Parse the date string (YYYY-MM-DD) correctly
    const [year, month, day] = dateStr.split('-').map(Number);
    const newDate = new Date(year, month - 1, day); // month is 0-indexed
    setCurrentDate(newDate);
    setView('day');
  };

  // Render appointment card
  const AppointmentCard = ({ apt, positioned, clickable }) => {
    const style = positioned ? getAppointmentStyle(apt.appointment_time, apt.service_duration) : {};

    const handleCardClick = (e) => {
      if (clickable) {
        e.stopPropagation();
        navigateToDay(apt.appointment_date);
      }
    };

    return (
      <div
        className={`appointment-card ${apt.status} ${positioned ? 'positioned' : ''} ${clickable ? 'clickable' : ''}`}
        style={positioned ? style : {}}
        onClick={handleCardClick}
      >
        <div className="apt-content">
          <div className="apt-time">{formatTime(apt.appointment_time)}</div>
          {apt.client_name && <div className="apt-client">{apt.client_name}</div>}
          {apt.services && apt.services.length > 0 ? (
            <>
              <div className="apt-service">
                {apt.services.length === 1
                  ? apt.services[0].name
                  : `${apt.services.length} Services`}
              </div>
              <div className="apt-duration">{apt.total_duration || apt.service_duration} min</div>
            </>
          ) : (
            <>
              {apt.service_name && <div className="apt-service">{apt.service_name}</div>}
              {apt.service_duration && <div className="apt-duration">{apt.service_duration} min</div>}
            </>
          )}
        </div>
        <div className="apt-actions" onClick={(e) => e.stopPropagation()}>
          {apt.status === 'scheduled' && (
            <>
              <button onClick={(e) => { e.stopPropagation(); updateStatus(apt.id, 'completed'); }} className="apt-btn complete" title="Complete">✓</button>
              <button onClick={(e) => { e.stopPropagation(); updateStatus(apt.id, 'cancelled'); }} className="apt-btn cancel" title="Cancel">✕</button>
            </>
          )}
          <button onClick={(e) => { e.stopPropagation(); deleteAppointment(apt.id); }} className="apt-btn delete" title="Delete">🗑</button>
        </div>
      </div>
    );
  };

  // Get time blocks for a specific date
  const getTimeBlocksForDate = (dateStr) => {
    return timeBlocks.filter(block => block.block_date === dateStr);
  };

  // Day View
  const DayView = () => {
    const dateStr = formatDateString(currentDate);
    const dayAppointments = getAppointmentsForDate(dateStr);
    const dayTimeBlocks = getTimeBlocksForDate(dateStr);
    const allHours = Array.from({ length: 24 }, (_, i) => i); // 12 AM to 11 PM (0-23)

    // Filter hours based on showAfterHours toggle
    const hours = showAfterHours
      ? allHours
      : allHours.filter(h => h >= 7 && h < 20); // 7 AM to 7 PM during standard hours

    const isAfterHours = (hour) => hour < 7 || hour >= 20;

    // Calculate the maximum duration to determine scaling
    const maxDuration = dayAppointments.length > 0
      ? Math.max(...dayAppointments.map(apt => apt.service_duration || 60))
      : 60;

    // Each minute should be at least 1.33px to fit button content
    // Scale up based on max duration so the largest card fits comfortably
    const pixelsPerMinute = Math.max(1.33, (maxDuration + 20) / 60);
    const baseHourHeight = 60 * pixelsPerMinute;

    const handleDragStart = (e, apt) => {
      setDraggedAppointment(apt);
      e.dataTransfer.effectAllowed = 'move';

      // Make the original element semi-transparent
      e.currentTarget.style.opacity = '0.4';
    };

    const handleDragEnd = (e) => {
      e.currentTarget.style.opacity = '1';
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e) => {
      e.preventDefault();
      if (!draggedAppointment) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const minutesFromTop = y / pixelsPerMinute;

      // Snap to 15-minute intervals (floor to snap to nearest upper interval)
      const snappedMinutes = Math.floor(minutesFromTop / 15) * 15;
      const totalMinutes = 8 * 60 + snappedMinutes;
      const newHours = Math.floor(totalMinutes / 60);
      const newMinutes = totalMinutes % 60;
      const newTime = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;

      updateAppointmentTime(draggedAppointment.id, dateStr, newTime);
      setDraggedAppointment(null);
    };

    return (
      <div className="calendar-view day-view">
        <div className="time-grid">
          <div className="time-labels">
            {hours.map(hour => {
              const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
              const period = hour >= 12 ? 'PM' : 'AM';
              const afterHoursClass = isAfterHours(hour) ? 'after-hours' : '';
              return (
                <div key={hour} className={`hour-label-group ${afterHoursClass}`} style={{ height: `${baseHourHeight}px` }}>
                  <div className="hour-label-main">
                    {displayHour}:00 {period}
                  </div>
                  <div className="minute-label">:15</div>
                  <div className="minute-label">:30</div>
                  <div className="minute-label">:45</div>
                </div>
              );
            })}
          </div>
          <div
            className="time-slots-container"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {/* 15-minute grid lines */}
            {hours.map(hour => {
              const afterHoursClass = isAfterHours(hour) ? 'after-hours' : '';
              return (
                <React.Fragment key={hour}>
                  <div className={`quarter-hour-slot ${afterHoursClass}`} style={{ height: `${baseHourHeight / 4}px` }}></div>
                  <div className={`quarter-hour-slot ${afterHoursClass}`} style={{ height: `${baseHourHeight / 4}px` }}></div>
                  <div className={`quarter-hour-slot ${afterHoursClass}`} style={{ height: `${baseHourHeight / 4}px` }}></div>
                  <div className={`quarter-hour-slot ${afterHoursClass}`} style={{ height: `${baseHourHeight / 4}px` }}></div>
                </React.Fragment>
              );
            })}
            {/* Appointments and Time Blocks positioned absolutely with overlap handling */}
            <div className="appointments-layer">
              {(() => {
                // Combine appointments and blocks into a unified array for overlap calculation
                const allItems = [
                  ...dayAppointments.map(apt => ({
                    type: 'appointment',
                    id: apt.id,
                    data: apt,
                    startTime: apt.appointment_time,
                    duration: apt.total_duration || apt.service_duration || 60
                  })),
                  ...dayTimeBlocks.map(block => {
                    const [blockHours, blockMinutes] = block.start_time.split(':').map(Number);
                    const [endHours, endMinutes] = block.end_time.split(':').map(Number);
                    const totalMinutes = blockHours * 60 + blockMinutes;
                    const endTotalMinutes = endHours * 60 + endMinutes;
                    return {
                      type: 'block',
                      id: `block-${block.id}`,
                      data: block,
                      startTime: block.start_time,
                      duration: endTotalMinutes - totalMinutes
                    };
                  })
                ];

                return allItems.map((item, idx) => {
                  const [itemHours, itemMinutes] = item.startTime.split(':').map(Number);
                  const totalMinutes = itemHours * 60 + itemMinutes;

                  // Calculate start based on first visible hour
                  const firstVisibleHour = hours.length > 0 ? hours[0] : 0;
                  const startMinutes = firstVisibleHour * 60;
                  const minutesFromStart = totalMinutes - startMinutes;

                  // Check for overlapping items (both appointments and blocks)
                  let overlapIndex = 0;
                  let overlapCount = 1;
                  const itemStart = totalMinutes;
                  const itemEnd = totalMinutes + item.duration;

                  // Find overlaps with all items
                  const overlappingItems = allItems.filter((other, otherIdx) => {
                    if (other.id === item.id) return false;
                    const [otherHours, otherMinutes] = other.startTime.split(':').map(Number);
                    const otherStart = otherHours * 60 + otherMinutes;
                    const otherEnd = otherStart + other.duration;
                    return (itemStart < otherEnd && itemEnd > otherStart);
                  });

                  overlapCount = overlappingItems.length + 1;

                  // Calculate position within overlapping group
                  overlappingItems.forEach(other => {
                    if (allItems.findIndex(a => a.id === other.id) < idx) {
                      overlapIndex++;
                    }
                  });

                  const width = overlapCount > 1 ? `${95 / overlapCount}%` : '95%';
                  const left = overlapCount > 1 ? `${(overlapIndex * 95) / overlapCount}%` : '2.5%';

                  // Position and size based on pixels per minute
                  const scaledStyle = {
                    top: `${minutesFromStart * pixelsPerMinute}px`,
                    height: `${item.duration * pixelsPerMinute}px`,
                    width: width,
                    left: left
                  };

                  if (item.type === 'appointment') {
                    const apt = item.data;
                    return (
                      <div
                        key={apt.id}
                        className={`appointment-card ${apt.status} positioned`}
                        style={scaledStyle}
                        draggable
                        onDragStart={(e) => handleDragStart(e, apt)}
                        onDragEnd={handleDragEnd}
                        onClick={() => openAppointmentModal(apt)}
                      >
                        <div className="apt-content">
                          <div className="apt-client">{apt.client_name}</div>
                        </div>
                      </div>
                    );
                  } else {
                    const block = item.data;
                    return (
                      <div
                        key={item.id}
                        className="time-block-card positioned"
                        style={scaledStyle}
                        title={block.reason || 'Blocked time'}
                      >
                        <div className="block-content">
                          <div className="block-label">🚫 BLOCKED</div>
                          {block.stylist_name && <div className="block-stylist">{block.stylist_name}</div>}
                          {block.reason && <div className="block-reason">{block.reason}</div>}
                        </div>
                        <button
                          className="block-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTimeBlock(block.id);
                          }}
                          title="Delete block"
                        >
                          ×
                        </button>
                      </div>
                    );
                  }
                });
              })()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Week View
  const WeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      return date;
    });

    const allHours = Array.from({ length: 24 }, (_, i) => i); // 12 AM to 11 PM (0-23)

    // Filter hours based on showAfterHours toggle
    const hours = showAfterHours
      ? allHours
      : allHours.filter(h => h >= 7 && h < 20); // 7 AM to 7 PM during standard hours

    const isAfterHours = (hour) => hour < 7 || hour >= 20;

    // Calculate max duration across all days in the week
    let maxDuration = 60;
    weekDays.forEach(date => {
      const dateStr = formatDateString(date);
      const dayAppointments = getAppointmentsForDate(dateStr);
      if (dayAppointments.length > 0) {
        const dayMax = Math.max(...dayAppointments.map(apt => apt.service_duration || 60));
        maxDuration = Math.max(maxDuration, dayMax);
      }
    });

    const pixelsPerMinute = Math.max(1.33, (maxDuration + 20) / 60);
    const baseHourHeight = 60 * pixelsPerMinute;

    const handleDragStart = (e, apt) => {
      setDraggedAppointment(apt);
      e.dataTransfer.effectAllowed = 'move';

      // Make the original element semi-transparent
      e.currentTarget.style.opacity = '0.4';
    };

    const handleDragEnd = (e) => {
      e.currentTarget.style.opacity = '1';
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, dateStr) => {
      e.preventDefault();
      e.stopPropagation();
      if (!draggedAppointment) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const minutesFromTop = y / pixelsPerMinute;

      // Snap to 15-minute intervals (floor to snap to nearest upper interval)
      const snappedMinutes = Math.floor(minutesFromTop / 15) * 15;
      const totalMinutes = 8 * 60 + snappedMinutes;
      const newHours = Math.floor(totalMinutes / 60);
      const newMinutes = totalMinutes % 60;
      const newTime = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;

      updateAppointmentTime(draggedAppointment.id, dateStr, newTime);
      setDraggedAppointment(null);
    };

    return (
      <div className="calendar-view week-view">
        <div className="week-header">
          <div className="time-column-header"></div>
          {weekDays.map((date, i) => {
            const isToday = formatDateString(date) === formatDateString(new Date());
            return (
              <div key={i} className={`day-header ${isToday ? 'today' : ''}`}>
                <div className="day-name">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i]}</div>
                <div className="day-number">{date.getDate()}</div>
              </div>
            );
          })}
        </div>
        <div className="week-grid-container">
          <div className="week-time-column">
            {hours.map(hour => {
              const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
              const period = hour >= 12 ? 'PM' : 'AM';
              const afterHoursClass = isAfterHours(hour) ? 'after-hours' : '';
              return (
                <div key={hour} className={`week-hour-label-group ${afterHoursClass}`} style={{ height: `${baseHourHeight}px` }}>
                  <div className="week-hour-main">
                    {displayHour} {period}
                  </div>
                  <div className="week-minute-label">:15</div>
                  <div className="week-minute-label">:30</div>
                  <div className="week-minute-label">:45</div>
                </div>
              );
            })}
          </div>
          <div className="week-days-grid">
            {weekDays.map((date, dayIndex) => {
              const dateStr = formatDateString(date);
              const dayAppointments = getAppointmentsForDate(dateStr);
              const dayTimeBlocks = getTimeBlocksForDate(dateStr);
              return (
                <div
                  key={dayIndex}
                  className="week-day-column"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, dateStr)}
                >
                  {/* 15-minute grid lines */}
                  {hours.map(hour => {
                    const afterHoursClass = isAfterHours(hour) ? 'after-hours' : '';
                    return (
                      <React.Fragment key={hour}>
                        <div className={`quarter-hour-slot ${afterHoursClass}`} style={{ height: `${baseHourHeight / 4}px` }}></div>
                        <div className={`quarter-hour-slot ${afterHoursClass}`} style={{ height: `${baseHourHeight / 4}px` }}></div>
                        <div className={`quarter-hour-slot ${afterHoursClass}`} style={{ height: `${baseHourHeight / 4}px` }}></div>
                        <div className={`quarter-hour-slot ${afterHoursClass}`} style={{ height: `${baseHourHeight / 4}px` }}></div>
                      </React.Fragment>
                    );
                  })}
                  {/* Appointments and Time Blocks positioned absolutely with overlap handling */}
                  <div className="week-appointments-layer">
                    {(() => {
                      // Combine appointments and blocks into a unified array for overlap calculation
                      const allItems = [
                        ...dayAppointments.map(apt => ({
                          type: 'appointment',
                          id: apt.id,
                          data: apt,
                          startTime: apt.appointment_time,
                          duration: apt.total_duration || apt.service_duration || 60
                        })),
                        ...dayTimeBlocks.map(block => {
                          const [blockHours, blockMinutes] = block.start_time.split(':').map(Number);
                          const [endHours, endMinutes] = block.end_time.split(':').map(Number);
                          const totalMinutes = blockHours * 60 + blockMinutes;
                          const endTotalMinutes = endHours * 60 + endMinutes;
                          return {
                            type: 'block',
                            id: `block-${block.id}`,
                            data: block,
                            startTime: block.start_time,
                            duration: endTotalMinutes - totalMinutes
                          };
                        })
                      ];

                      return allItems.map((item, idx) => {
                        const [itemHours, itemMinutes] = item.startTime.split(':').map(Number);
                        const totalMinutes = itemHours * 60 + itemMinutes;

                        // Calculate start based on first visible hour
                        const firstVisibleHour = hours.length > 0 ? hours[0] : 0;
                        const startMinutes = firstVisibleHour * 60;
                        const minutesFromStart = totalMinutes - startMinutes;

                        // Check for overlapping items (both appointments and blocks)
                        let overlapIndex = 0;
                        let overlapCount = 1;
                        const itemStart = totalMinutes;
                        const itemEnd = totalMinutes + item.duration;

                        // Find overlaps with all items
                        const overlappingItems = allItems.filter(other => {
                          if (other.id === item.id) return false;
                          const [otherHours, otherMinutes] = other.startTime.split(':').map(Number);
                          const otherStart = otherHours * 60 + otherMinutes;
                          const otherEnd = otherStart + other.duration;
                          return (itemStart < otherEnd && itemEnd > otherStart);
                        });

                        overlapCount = overlappingItems.length + 1;

                        // Calculate position within overlapping group
                        overlappingItems.forEach(other => {
                          if (allItems.findIndex(a => a.id === other.id) < idx) {
                            overlapIndex++;
                          }
                        });

                        const width = overlapCount > 1 ? `${95 / overlapCount}%` : '95%';
                        const left = overlapCount > 1 ? `${(overlapIndex * 95) / overlapCount}%` : '2.5%';

                        // Position and size based on pixels per minute
                        const scaledStyle = {
                          top: `${minutesFromStart * pixelsPerMinute}px`,
                          height: `${item.duration * pixelsPerMinute}px`,
                          width: width,
                          left: left
                        };

                        if (item.type === 'appointment') {
                          const apt = item.data;
                          return (
                            <div
                              key={apt.id}
                              className={`appointment-card ${apt.status} positioned`}
                              style={scaledStyle}
                              draggable
                              onDragStart={(e) => handleDragStart(e, apt)}
                              onDragEnd={handleDragEnd}
                              onClick={() => openAppointmentModal(apt)}
                            >
                              <div className="apt-content">
                                <div className="apt-client">{apt.client_name}</div>
                              </div>
                            </div>
                          );
                        } else {
                          const block = item.data;
                          return (
                            <div
                              key={item.id}
                              className="time-block-card positioned"
                              style={scaledStyle}
                              title={block.reason || 'Blocked time'}
                            >
                              <div className="block-content">
                                <div className="block-label">🚫 BLOCKED</div>
                                {block.stylist_name && <div className="block-stylist">{block.stylist_name}</div>}
                                {block.reason && <div className="block-reason">{block.reason}</div>}
                              </div>
                              <button
                                className="block-delete-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTimeBlock(block.id);
                                }}
                                title="Delete block"
                              >
                                ×
                              </button>
                            </div>
                          );
                        }
                      });
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Month View
  const MonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    // Empty cells before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="month-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDateString(date);
      const dayAppointments = getAppointmentsForDate(dateStr);
      const dayTimeBlocks = getTimeBlocksForDate(dateStr);
      const isToday = dateStr === formatDateString(new Date());

      days.push(
        <div key={day} className={`month-day ${isToday ? 'today' : ''}`}>
          <div className="day-number">{day}</div>
          <div className="day-appointments">
            {/* Combine and sort appointments and time blocks by time */}
            {(() => {
              const combinedItems = [
                ...dayAppointments.map(apt => ({
                  type: 'appointment',
                  time: apt.appointment_time,
                  data: apt
                })),
                ...dayTimeBlocks.map(block => ({
                  type: 'block',
                  time: block.start_time,
                  data: block
                }))
              ];

              // Sort by time
              combinedItems.sort((a, b) => a.time.localeCompare(b.time));

              // Take first 3 items
              const itemsToShow = combinedItems.slice(0, 3);

              return itemsToShow.map((item, idx) => {
                if (item.type === 'block') {
                  return (
                    <div
                      key={`block-${item.data.id}`}
                      className="month-block"
                      onClick={() => navigateToDay(dateStr)}
                      style={{ cursor: 'pointer' }}
                    >
                      🚫 {formatTime(item.data.start_time)}-{formatTime(item.data.end_time)} {item.data.stylist_name ? `(${item.data.stylist_name})` : '(All)'}
                    </div>
                  );
                } else {
                  return (
                    <div
                      key={item.data.id}
                      className={`month-apt ${item.data.status}`}
                      onClick={() => navigateToDay(dateStr)}
                      style={{ cursor: 'pointer' }}
                    >
                      {formatTime(item.data.appointment_time)} - {item.data.client_name}
                    </div>
                  );
                }
              });
            })()}
            {(dayAppointments.length + dayTimeBlocks.length) > 3 && (
              <div
                className="more-apts"
                onClick={() => navigateToDay(dateStr)}
                style={{ cursor: 'pointer' }}
              >
                +{(dayAppointments.length + dayTimeBlocks.length) - 3} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="calendar-view month-view">
        <div className="month-grid">
          <div className="month-day-name">Sun</div>
          <div className="month-day-name">Mon</div>
          <div className="month-day-name">Tue</div>
          <div className="month-day-name">Wed</div>
          <div className="month-day-name">Thu</div>
          <div className="month-day-name">Fri</div>
          <div className="month-day-name">Sat</div>
          {days}
        </div>
      </div>
    );
  };

  const getHeaderTitle = () => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];

    if (view === 'day') {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
    } else if (view === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return `${monthNames[startOfWeek.getMonth()]} ${startOfWeek.getDate()} - ${monthNames[endOfWeek.getMonth()]} ${endOfWeek.getDate()}, ${currentDate.getFullYear()}`;
    } else {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
  };

  return (
    <div className="appointments">
      <div className="appointments-header">
        <h2>Appointments</h2>
        <div className="view-controls">
          <button onClick={() => setShowBlockModal(true)} className="block-time-btn" style={{ marginRight: '15px' }}>
            Block Time
          </button>
          <div className="view-switcher">
            <button className={view === 'day' ? 'active' : ''} onClick={() => setView('day')}>Day</button>
            <button className={view === 'week' ? 'active' : ''} onClick={() => setView('week')}>Week</button>
            <button className={view === 'month' ? 'active' : ''} onClick={() => setView('month')}>Month</button>
          </div>
        </div>
      </div>

      <div className="calendar-controls">
        <div className="filter-buttons">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All</button>
          <button className={filter === 'pending' ? 'active' : ''} onClick={() => setFilter('pending')}>Pending</button>
          <button className={filter === 'scheduled' ? 'active' : ''} onClick={() => setFilter('scheduled')}>Confirmed</button>
          <button className={filter === 'completed' ? 'active' : ''} onClick={() => setFilter('completed')}>Completed</button>
          <button className={filter === 'cancelled' ? 'active' : ''} onClick={() => setFilter('cancelled')}>Cancelled</button>
        </div>
        <div className="stylist-filter">
          <label>Stylist: </label>
          <select value={stylistFilter} onChange={(e) => setStylistFilter(e.target.value)}>
            <option value="all">All Stylists</option>
            <option value="unassigned">Unassigned</option>
            {stylists.map(stylist => (
              <option key={stylist.id} value={stylist.id}>{stylist.name}</option>
            ))}
          </select>
        </div>
        <div className="after-hours-toggle">
          <label>
            <input
              type="checkbox"
              checked={showAfterHours}
              onChange={(e) => setShowAfterHours(e.target.checked)}
            />
            Show After Hours (12-7AM, 8-11PM)
          </label>
        </div>
        <div className="nav-controls">
          <button onClick={() => navigate(-1)} className="nav-btn">&lt;</button>
          <button onClick={goToToday} className="today-btn">Today</button>
          <button onClick={() => navigate(1)} className="nav-btn">&gt;</button>
          <span className="current-period">{getHeaderTitle()}</span>
        </div>
      </div>

      {view === 'day' && <DayView />}
      {view === 'week' && <WeekView />}
      {view === 'month' && <MonthView />}

      {/* Appointment Detail Modal */}
      {selectedAppointment && editedAppointment && (
        <div className="modal-overlay" onClick={closeAppointmentModal}>
          <div className="appointment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Appointment Details</h3>
              <button className="modal-close" onClick={closeAppointmentModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="appointment-details-section">
                <div className="form-group">
                  <label>Client</label>
                  <div className="read-only">{selectedAppointment.client_name}</div>
                </div>
              </div>
              <div className="appointment-edit-section">
                <div className="form-group">
                  <label>Stylist</label>
                  <select
                    value={editedAppointment.stylist_id || ''}
                    onChange={(e) => setEditedAppointment({ ...editedAppointment, stylist_id: e.target.value ? parseInt(e.target.value) : null })}
                  >
                    <option value="">No stylist assigned</option>
                    {stylists.map(stylist => (
                      <option key={stylist.id} value={stylist.id}>{stylist.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Services</label>
                  <div className="service-add-row">
                    <select
                      value={serviceToAdd}
                      onChange={(e) => setServiceToAdd(e.target.value)}
                      className="service-dropdown"
                    >
                      <option value="">Choose a service...</option>
                      {services.map(service => (
                        <option key={service.id} value={service.id}>
                          {service.name} - ${service.price} ({service.duration} min)
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleAddServiceToAppointment}
                      className="btn-add-service"
                      disabled={!serviceToAdd}
                    >
                      Add Service
                    </button>
                  </div>

                  {editedServices.length > 0 && (
                    <div className="selected-services-list">
                      <h4>Selected Services:</h4>
                      {editedServices.map((service, index) => (
                        <div key={index} className="selected-service-item">
                          <span className="service-name">{service.name}</span>
                          <span className="service-details">${service.price} • {service.duration} min</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveServiceFromAppointment(index)}
                            className="btn-remove-service"
                          >
                            ×
                          </button>
                        </div>
                      ))}

                      <div className="services-summary-box">
                        <div className="summary-row">
                          <span>Total Services:</span>
                          <strong>{editedServices.length}</strong>
                        </div>
                        <div className="summary-row">
                          <span>Total Price:</span>
                          <strong>${editedServices.reduce((sum, s) => sum + parseFloat(s.price), 0).toFixed(2)}</strong>
                        </div>
                        <div className="summary-row">
                          <span>Total Duration:</span>
                          <input
                            type="number"
                            value={editedTotalDuration}
                            onChange={(e) => setEditedTotalDuration(parseInt(e.target.value) || 0)}
                            className="duration-input"
                            min="0"
                          />
                          <span>minutes</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    value={editedAppointment.appointment_date}
                    onChange={(e) => setEditedAppointment({ ...editedAppointment, appointment_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Time</label>
                  <input
                    type="time"
                    value={editedAppointment.appointment_time}
                    onChange={(e) => setEditedAppointment({ ...editedAppointment, appointment_time: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={editedAppointment.status}
                    onChange={(e) => setEditedAppointment({ ...editedAppointment, status: e.target.value })}
                  >
                    <option value="pending">Pending Confirmation</option>
                    <option value="scheduled">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={editedAppointment.notes || ''}
                    onChange={(e) => setEditedAppointment({ ...editedAppointment, notes: e.target.value })}
                    placeholder="Add notes..."
                  />
                </div>
              </div>

              {/* Payment Information Section */}
              {appointmentPayments.length > 0 && (
                <div className="appointment-payments-section">
                  <h4>Payment Information</h4>
                  {appointmentPayments.map(payment => (
                    <div key={payment.id} className="payment-info-card">
                      <div className="payment-info-row">
                        <span className="payment-label">Amount:</span>
                        <span className="payment-value">{formatCurrency(payment.amount)}</span>
                      </div>
                      {payment.tip_amount > 0 && (
                        <div className="payment-info-row">
                          <span className="payment-label">Tip:</span>
                          <span className="payment-value">{formatCurrency(payment.tip_amount)}</span>
                        </div>
                      )}
                      {payment.discount_amount > 0 && (
                        <div className="payment-info-row">
                          <span className="payment-label">Discount:</span>
                          <span className="payment-value">-{formatCurrency(payment.discount_amount)}</span>
                        </div>
                      )}
                      <div className="payment-info-row">
                        <span className="payment-label">Total:</span>
                        <span className="payment-value payment-total">
                          {formatCurrency(parseFloat(payment.amount) + parseFloat(payment.tip_amount || 0) - parseFloat(payment.discount_amount || 0))}
                        </span>
                      </div>
                      <div className="payment-info-row">
                        <span className="payment-label">Status:</span>
                        <span className={`status-badge ${getPaymentStatusBadge(payment.status).class}`}>
                          {getPaymentStatusBadge(payment.status).text}
                        </span>
                      </div>
                      <div className="payment-info-row">
                        <span className="payment-label">Method:</span>
                        <span className="payment-value">{payment.payment_method || 'Card'}</span>
                      </div>
                      {payment.receipt_url && (
                        <div className="payment-info-row">
                          <a href={payment.receipt_url} target="_blank" rel="noopener noreferrer" className="btn-small">
                            View Receipt
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {appointmentPayments.length === 0 && selectedAppointment.status === 'scheduled' && (
                <div className="no-payment-notice">
                  <p>No payment recorded for this appointment.</p>
                  <Link
                    to="/dashboard/payments"
                    state={{ appointmentData: selectedAppointment }}
                    className="btn-small"
                  >
                    Process Payment
                  </Link>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeAppointmentModal}>Cancel</button>
              <button className="btn-danger" onClick={() => { deleteAppointment(selectedAppointment.id); closeAppointmentModal(); }}>Delete</button>
              <button className="btn-primary" onClick={updateAppointmentDetails}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Block Time Modal */}
      {showBlockModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Block Time</h3>
            <form onSubmit={handleCreateTimeBlock}>
              <div className="form-group">
                <label>Stylist (Optional)</label>
                <select
                  value={blockFormData.stylist_id}
                  onChange={(e) => setBlockFormData({...blockFormData, stylist_id: e.target.value})}
                >
                  <option value="">All Stylists</option>
                  {stylists.map(stylist => (
                    <option key={stylist.id} value={stylist.id}>{stylist.name}</option>
                  ))}
                </select>
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  Leave blank to block time for all stylists
                </small>
              </div>

              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={blockFormData.block_date}
                  onChange={(e) => setBlockFormData({...blockFormData, block_date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Time *</label>
                  <input
                    type="time"
                    value={blockFormData.start_time}
                    onChange={(e) => setBlockFormData({...blockFormData, start_time: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>End Time *</label>
                  <input
                    type="time"
                    value={blockFormData.end_time}
                    onChange={(e) => setBlockFormData({...blockFormData, end_time: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Reason (Optional)</label>
                <textarea
                  value={blockFormData.reason}
                  onChange={(e) => setBlockFormData({...blockFormData, reason: e.target.value})}
                  placeholder="e.g., Lunch break, Staff meeting, Closed"
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="submit">Block Time</button>
                <button type="button" onClick={() => {
                  setShowBlockModal(false);
                  setBlockFormData({
                    stylist_id: '',
                    block_date: '',
                    start_time: '',
                    end_time: '',
                    reason: ''
                  });
                }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Appointments;
